import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import {
  ModelViewer,
  ModelSelector,
  Gui,
  PushPins,
  Components,
  Measure,
  FirstPerson,
  HomeView,
  ViewerSettings,
  THEME_AVT_DARK,
  THEME_AVT_BRIGHT,
} from '@iq/model-viewer';

import '@iq/model-viewer/dist/index.css';

import { useTheme } from '@iq/react-components';
import {
  shouldRenderComponent,
  shouldHighlightComponent,
  getComponentOverlay,
  shouldShowPin,
  getPinStyle,
  getPushpinTooltipContent,
  getComponentsTooltipContent,
} from './helpers';

import {
  requestMappings,
  requestGeometries,
  getIndexedMappingsBySite,
  getModelAndDesignationIndexedMappingIdsBySite,
} from '../../../bundles/geometry-mappings';
import {
  getPageComponents,
  getStaticComponents,
  getComponentTree,
  getFilteredActiveComponentId,
} from '../../../bundles/components';
import {
  removeSubscriptions as removeVariableSubscriptions,
  getLastVariableData,
  refreshLastVariableData,
} from '../../../bundles/variables';
import { getSiteVariables } from '../../../bundles/sources';
import {
  setActiveComponentId,
  getActiveModelId,
  setActiveModelId,
  setViewerReady,
  getZoomComponentId,
  setZoomComponentId,
  getPollingDateRange,
  getTimezone,
  getPageType,
} from '../../../bundles/application';
import { getAllModels } from '../../../bundles/models';
import { getActiveSite } from '../../../bundles/sites';
import { useClientSize, useVariableSubscription } from '../../../utils';
import { getServiceToken } from '../../../services';

import ConfirmationDialog from '../../ConfirmationDialog';
import PermissionDenied from '../../PermissionDenied';
import { getStateSets, findActiveStatesForVariables } from '../../../bundles/statesets';

const ViewerPanel = (properties) => {
  const { id: siteId } = useParams();
  const {
    pageIndex,
    panelId,
    display = true,
    pushPins: rawPushPins,
    componentTooltips: rawComponentTooltips,
    tooltipOnHover = false,
    showAreaObjects = false,
    isolate = true,
    renderComponents,
    highlightComponents,
    overlayComponents,
    showPushPins,
    forceErrorPins: displayErrorPins,
    selectionType,
    focusActiveComponent,
    showActivePushpinTooltip,
    toolbarOptions = ['home', 'measure', 'firstperson'],
    modelSourceComponent: modelSourceComponentId,
    modelSourceComponentDefaultModel,
  } = properties;
  const dispatch = useDispatch();
  const theme = useTheme();
  const timezone = useSelector(getTimezone);
  const pageType = useSelector(getPageType);
  const site = useSelector(getActiveSite);
  const activeComponentId = useSelector(getFilteredActiveComponentId);
  const components = useSelector(getPageComponents);
  const siteVariables = useSelector(getSiteVariables);
  const staticComponents = useSelector(getStaticComponents);
  const componentTree = useSelector(getComponentTree);
  const modelsHashmap = useSelector(getAllModels);
  const zoomComponentId = useSelector(getZoomComponentId);
  const activeModelId = useSelector(getActiveModelId);
  const indexedMappings = useSelector((state) => getIndexedMappingsBySite(state, siteId));
  const indexedMappingIds = useSelector((state) =>
    getModelAndDesignationIndexedMappingIdsBySite(state, siteId)
  );
  const latestVariableData = useSelector(getLastVariableData);
  const stateSets = useSelector(getStateSets);
  const { endDate, startDate } = useSelector(getPollingDateRange);

  const [pollingVariables, setPollingVariables] = useState([]);
  const [modelIndexByPage, setModelIndexByPage] = useState({});
  const [viewerClickedId, setViewerClickedId] = useState(null);
  const [versionConfig, setVersionConfig] = useState(null);
  const [focusedComponentIds, setFocusedComponentIds] = useState([]);
  const [visibleTooltip, setVisibleTooltip] = useState();
  const [fitToIds, setFitToIds] = useState([]);
  const [modelLoading, setModelLoading] = useState(true);
  const [clientSize, clientReference] = useClientSize();
  const [height, setHeight] = useState(clientSize.height);

  const initialModelPropose = { show: false, model: null, ignore: false };
  const [proposeModelChange, setProposeModelChange] = useState(initialModelPropose);
  const prevDefaultModelIdByPageRef = useRef({});

  useVariableSubscription(panelId, pollingVariables);

  useEffect(() => {
    setHeight(clientSize.height);
  }, [clientSize]);

  useEffect(() => {
    dispatch(requestMappings(siteId));
    dispatch(setActiveModelId(null));
  }, [siteId]);

  const siteVariableIds = useMemo(() => siteVariables.map((v) => v.id), [siteVariables]);
  const pushPins = useMemo(
    () =>
      (rawPushPins || []).map((pin) => ({
        ...pin,
        variables: (pin.variables || []).filter((v) => siteVariableIds.includes(v.variableId)),
      })),
    [rawPushPins, siteVariableIds]
  );
  const componentTooltips = useMemo(
    () =>
      (rawComponentTooltips || []).map((tooltip) => ({
        ...tooltip,
        variables: (tooltip.variables || []).filter((v) => siteVariableIds.includes(v.variableId)),
      })),
    [rawComponentTooltips, siteVariableIds]
  );

  const getModelSourceComponent = useCallback(() => {
    if (staticComponents.length > 0) {
      let component = null;

      if (modelSourceComponentId) {
        component = staticComponents.find((c) => c.id === modelSourceComponentId);
      }

      // If no component found by ID, or component has no models, use root component
      if (!component || !component?.models?.length) {
        component = staticComponents.find(
          (c) => c?.type === 'site' && (!('parent' in c) || c?.parent === null)
        );
      }

      return component;
    }
    return null;
  }, [staticComponents, modelSourceComponentId]);

  const modelSourceComponent = useMemo(() => getModelSourceComponent(), [getModelSourceComponent]);
  const hasSourceModels = useMemo(
    () => modelSourceComponent?.models?.length > 0,
    [modelSourceComponent]
  );

  const getDefaultModelId = useCallback(
    (component) => {
      if (!component?.models?.length) return null;

      if (modelSourceComponentDefaultModel) {
        const hasDefaultModel = component.models.some(
          (m) => m.modelId === modelSourceComponentDefaultModel
        );
        if (hasDefaultModel) {
          const siteModels = Object.values(modelsHashmap);
          const modelExists = siteModels.some(
            (siteModel) => siteModel.id === modelSourceComponentDefaultModel
          );
          if (modelExists) {
            return modelSourceComponentDefaultModel;
          }
        }
      }

      return component.models[0].modelId;
    },
    [modelSourceComponentDefaultModel, modelsHashmap]
  );

  // Prepare model data for the viewer, defining selectables and root model
  const getModelData = useCallback(
    (component) => {
      const siteModels = Object.values(modelsHashmap);
      if (siteModels.length === 0 || !component?.models?.length) return [];

      const rootId = getDefaultModelId(component);
      const selectables = new Set(component.models.map((m) => m.modelId));

      return siteModels
        .map((model) => {
          const [version] = model.versions
            .filter((v) => v.status === 'COMPLETED')
            .toSorted((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          const [output] = version?.outputs?.filter((o) => o.type === 'svf') || [];

          if (!output) return null;

          return {
            ...model,
            urn: output.url,
            config: version.sceneConfig,
            root: model.id === rootId,
            customName: component.models.find((m) => m.modelId === model.id)?.customName,
            selectable: selectables.has(model.id),
          };
        })
        .filter(Boolean);
    },
    [modelsHashmap, getDefaultModelId]
  );

  const models = useMemo(
    () => getModelData(modelSourceComponent),
    [modelSourceComponent, getModelData]
  );

  // Handle page changes
  useEffect(
    () => {
      const defaultModelIdForCurrentPage = getDefaultModelId(modelSourceComponent);
      const defaultModelIndexForCurrentPage = models.findIndex(
        (m) => m.id === defaultModelIdForCurrentPage
      );
      const prevDefaultModelIdForPage = prevDefaultModelIdByPageRef.current[pageIndex];
      const hasIndexForPage = modelIndexByPage[pageIndex] >= 0;
      const defaultModelIdChanged = prevDefaultModelIdForPage !== defaultModelIdForCurrentPage;
      const modelIdForPage = hasIndexForPage && models[modelIndexByPage[pageIndex]]?.id;
      const selectables = modelSourceComponent?.models?.map((m) => m.modelId) || [];

      if (!hasIndexForPage && defaultModelIndexForCurrentPage !== -1) {
        // Handle switch to unviewed page
        setModelIndexByPage((previous) => ({
          ...previous,
          [pageIndex]: defaultModelIndexForCurrentPage,
        }));
        dispatch(setActiveModelId(defaultModelIdForCurrentPage));
      } else if (hasIndexForPage && defaultModelIdChanged) {
        dispatch(setActiveModelId(defaultModelIdForCurrentPage));
      } else if (hasIndexForPage && modelIdForPage && !defaultModelIdChanged) {
        // Handle switch to viewed page and if selected model unlinked go back to its default model
        dispatch(
          setActiveModelId(
            selectables.includes(modelIdForPage) ? modelIdForPage : defaultModelIdForCurrentPage
          )
        );
      }

      // Update the ref with current value for this page for next comparison
      prevDefaultModelIdByPageRef.current[pageIndex] = defaultModelIdForCurrentPage;
    },
    // don't add modelIndexByPage to the dependency array to prevent loop if it's updated on activeModelId changes
    [pageIndex, modelSourceComponent, models, getDefaultModelId, dispatch]
  );

  // Handle model changes NOT triggered by page changes (e.g. selector or comp panel icon)
  useEffect(
    () => {
      const hasIndexForPage = modelIndexByPage[pageIndex] >= 0;
      const activeModelIndex = models.findIndex((m) => m.id === activeModelId);
      const isActiveModelChange =
        hasIndexForPage &&
        activeModelIndex !== -1 &&
        activeModelIndex !== modelIndexByPage[pageIndex];

      if (isActiveModelChange) {
        setModelIndexByPage((previous) => ({
          ...previous,
          [pageIndex]: activeModelIndex,
        }));
      }
    },
    // don't add pageIndex to the dependency array as we only want this to be triggered by model changes
    [activeModelId, models, modelIndexByPage]
  );

  // Set the default versionConfig based on the user-defined model source component
  useEffect(() => {
    if (
      modelSourceComponent &&
      modelSourceComponent.models &&
      modelSourceComponent.models.length > 0 &&
      models.length > 0
    ) {
      const defaultModelId = getDefaultModelId(modelSourceComponent);
      const siteModel = models.find((model) => model.id === defaultModelId);
      if (siteModel) {
        const version = siteModel.versions
          .toSorted((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .find((v) => v.status === 'COMPLETED');
        if (version) {
          setVersionConfig(version.sceneConfig || {});
        }
      }
    }
  }, [modelSourceComponent, models, getDefaultModelId]);

  // Override the versionConfig if the activeModelId changes
  useEffect(() => {
    const model = models.find((m) => m.id === activeModelId);

    if (!model) return;

    const version = model.versions
      .toSorted((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .find((v) => v.status === 'COMPLETED');
    if (version) {
      setVersionConfig(version.sceneConfig || {});
    }
  }, [models, activeModelId]);

  useEffect(() => {
    if (viewerClickedId === null || viewerClickedId === activeComponentId) return;
    setViewerClickedId(null);
  }, [activeComponentId]);

  useEffect(() => {
    if (siteId && activeModelId) dispatch(requestGeometries(siteId, activeModelId));
  }, [siteId, activeModelId]);

  const mappedCompDesignationsSet = useMemo(() => {
    if (!indexedMappings) return new Set();
    return Object.values(indexedMappings).reduce((acc, current) => {
      for (const designation of current.itemDesignations) acc.add(designation);
      return acc;
    }, new Set());
  }, [indexedMappings]);

  useEffect(() => {
    const variableIdsToPoll = new Set();
    // reduce mapped page comps to Set of their stateful variables (from statesets)
    components.reduce((acc, current) => {
      if (current.variables.length > 0 && mappedCompDesignationsSet.has(current.itemDesignation)) {
        for (const variable of current.variables) acc.add(variable.id);
      }
      return acc;
    }, variableIdsToPoll);
    // reduce mapped push pins to Set of their variables
    for (const pin of pushPins) {
      if (
        pin.variables &&
        pin.variables.length > 0 &&
        mappedCompDesignationsSet.has(pin.itemDesignation)
      ) {
        for (const variable of pin.variables) variableIdsToPoll.add(variable.variableId);
      }
    }
    // reduce mapped component tooltips to Set of their configured variables - these can
    // reference variables that don't belong to the component they're attached to.
    for (const tooltip of componentTooltips) {
      if (
        tooltip.variables &&
        tooltip.variables.length > 0 &&
        mappedCompDesignationsSet.has(tooltip.itemDesignation)
      ) {
        for (const variable of tooltip.variables) variableIdsToPoll.add(variable.variableId);
      }
    }
    const variablesToCancelPollingOn = [];
    setPollingVariables((previousVariables) => {
      variablesToCancelPollingOn.push(
        ...previousVariables.filter((v) => !variableIdsToPoll.has(v))
      );
      return [...variableIdsToPoll];
    });
    if (variablesToCancelPollingOn.length > 0) {
      dispatch(removeVariableSubscriptions(panelId, variablesToCancelPollingOn));
    }
    // include pageIndex in the dependency array to re-run this effect when user switches pages
  }, [components, mappedCompDesignationsSet, pushPins, componentTooltips, pageIndex, panelId]);

  const idMappedComponents = useMemo(() => {
    let comps = [];
    // return empty array of comps at first to let the viewer load models first
    if (Array.isArray(components) && indexedMappingIds && models) {
      comps = components.map((comp) => {
        const mappedComponent = { ...comp, mappingsByModel: {} };
        for (const model of models) {
          const mappingId = indexedMappingIds[`${model.id}@${comp.itemDesignation}`];
          if (mappingId) {
            mappedComponent.mappingsByModel = {
              ...mappedComponent.mappingsByModel,
              [model.id]: mappingId,
            };
          }
        }
        return mappedComponent;
      });
    }
    return comps;
  }, [components, models, indexedMappingIds]);

  const mappedComponents = useMemo(() => {
    let comps = [];
    if (idMappedComponents && indexedMappings && activeModelId) {
      comps = idMappedComponents.map((comp) => {
        const mappingId = comp.mappingsByModel[activeModelId];
        const {
          dbIds: databaseIds = [],
          objects = [],
          position,
        } = indexedMappings[mappingId] || {};

        const stateSet = stateSets.find((s) => s.id === comp.stateset_id);

        return { ...comp, dbIds: databaseIds, objects, position, mappingId, stateSet };
      });
    }
    return comps;
  }, [idMappedComponents, indexedMappings, activeModelId, stateSets]);

  const activeComponent = useMemo(
    () => mappedComponents && mappedComponents.find((c) => c.id === activeComponentId),
    [pageIndex, mappedComponents, activeComponentId]
  );

  const activeModel = useMemo(
    () => models && models.find((m) => m.id === activeModelId),
    [pageIndex, models, activeModelId]
  );

  const findMappedChildrenIds = useCallback(() => {
    if (activeComponent && activeComponent.descendantIds) {
      return activeComponent.descendantIds.filter((id) => {
        const comp = mappedComponents.find((c) => c.id === id);
        return comp && comp.mappingsByModel[activeModelId];
      });
    }
    return [];
  }, [activeComponent, mappedComponents]);

  const findNearestAncestorWithModels = (parentId) => {
    const parent = mappedComponents.find((c) => c.id === parentId);
    if (parent && parent.models && parent.models.length > 0) {
      return parent;
    }
    if (parent && parent.parent) {
      return findNearestAncestorWithModels(parent.parent);
    }
    return null;
  };

  const getNearestMappedAncestorModel = useCallback(() => {
    let mappedModel = null;

    if (activeComponent && activeComponent.parent) {
      const nearestAncestorWithModels = findNearestAncestorWithModels(activeComponent.parent);
      if (nearestAncestorWithModels) {
        for (const model of nearestAncestorWithModels.models) {
          if (
            !mappedModel &&
            (indexedMappingIds || {})[`${model.modelId}@${activeComponent.itemDesignation}`]
          ) {
            mappedModel = model;
          }
        }
      }
    }
    return mappedModel;
  }, [activeComponent]);

  const findMappedChildModelId = useCallback(
    (node = componentTree, compId = activeComponent.id, foundComp = false) => {
      // finds first child with mapping and returns the modelId of the model it's mapped to
      let modelId = null;
      let found = foundComp;

      if (!node || !compId) return modelId;
      if (node.id === compId) {
        found = true;
      }
      if (found) {
        const comp = mappedComponents.find((c) => c.id === compId);
        const compMappedModels = comp && Object.keys(comp.mappingsByModel);
        const hasMapping = compMappedModels && compMappedModels.length > 0;
        if (hasMapping) {
          [modelId] = compMappedModels;
        } else if (node.children) {
          for (const child of node.children) {
            if (!modelId) {
              modelId = findMappedChildModelId(child, child.id, found);
            }
          }
        }
      } else if (node.children) {
        for (const child of node.children) {
          if (!modelId) {
            modelId = findMappedChildModelId(child, compId, found);
          }
        }
      }
      return modelId;
    },
    [componentTree, activeComponent]
  );

  const noShowNoIgnoreChange = { ...proposeModelChange, show: false, ignore: false };

  useEffect(() => {
    if (activeComponent) {
      let foundMappings = false;
      let foundAlternateModelMapping = false;

      if (activeComponent.mappingsByModel[activeModelId]) {
        foundMappings = true;
        setFocusedComponentIds([activeComponent.id]);
        setProposeModelChange(() => noShowNoIgnoreChange);
      }
      if (!foundMappings) {
        const mappedChildrenIds = findMappedChildrenIds();
        if (mappedChildrenIds.length > 0) {
          foundMappings = true;
          setFocusedComponentIds([activeComponent.id, ...mappedChildrenIds]);
          setProposeModelChange(() => noShowNoIgnoreChange);
        }
      }

      // Ignore checks if model is on the selected component.
      if (activeComponent.models.map((m) => m.modelId).includes(activeModelId)) {
        setFocusedComponentIds([activeComponent.id]);
        return;
      }

      if (!foundMappings && activeComponent.parent) {
        // find ancestor mapping if available
        const mappedAncestorModel = getNearestMappedAncestorModel();
        if (mappedAncestorModel) {
          foundAlternateModelMapping = true;
          setFocusedComponentIds([activeComponent.id]);
          if (
            !proposeModelChange.model ||
            proposeModelChange.model.id !== mappedAncestorModel.id ||
            proposeModelChange.model.modelId !== activeModel.id
          ) {
            setProposeModelChange(() => ({
              ignore:
                proposeModelChange.ignore &&
                proposeModelChange.model &&
                proposeModelChange.model.id === mappedAncestorModel.id,
              show: true,
              model: mappedAncestorModel,
            }));
          }
        }
      }
      if (!foundMappings && !foundAlternateModelMapping) {
        // we take first found child with mapping, and it's mapped model
        const mappedChildModelId = findMappedChildModelId();
        if (mappedChildModelId) {
          const modelComp = mappedComponents.find((comp) =>
            comp.models.map((model) => model.modelId).includes(mappedChildModelId)
          );
          if (modelComp) {
            const mappedDescendantModel = modelComp.models.find(
              (m) => m.modelId === mappedChildModelId
            );
            foundAlternateModelMapping = true;
            setFocusedComponentIds([activeComponent.id]);
            if (
              !proposeModelChange.model ||
              proposeModelChange.model.id !== mappedDescendantModel.id ||
              proposeModelChange.model.modelId !== activeModel.id
            ) {
              setProposeModelChange(() => ({
                ignore:
                  proposeModelChange.ignore &&
                  proposeModelChange.model &&
                  proposeModelChange.model.id === mappedDescendantModel.id,
                show: true,
                model: mappedDescendantModel,
              }));
            }
          }
        }
      }
      if (!foundMappings && !foundAlternateModelMapping) {
        // site level
        setFocusedComponentIds([activeComponent.id]);
        setProposeModelChange(() => noShowNoIgnoreChange);
      }
    } else {
      setFocusedComponentIds([]);
      setProposeModelChange(() => noShowNoIgnoreChange);
    }
  }, [
    activeComponent,
    activeModelId,
    componentTree,
    findMappedChildModelId,
    getNearestMappedAncestorModel,
  ]);

  useEffect(() => {
    if (zoomComponentId && zoomComponentId === activeComponentId) {
      setFitToIds(() => focusedComponentIds);
    } else {
      setFitToIds(() => []);
    }
  }, [zoomComponentId]);

  useEffect(() => {
    if (endDate && startDate) {
      dispatch(refreshLastVariableData());
    }
  }, [endDate, startDate]);

  const mapPushPinValues = useCallback(
    (pushPin) => {
      if (pushPin && pushPin.variables) {
        return {
          ...pushPin,
          variables: pushPin.variables.map((variable) => {
            const { variableId, name, unit } = variable;
            // We have already verified that pushpin variables exist when defining
            const sourceVariable = siteVariables.find((v) => v.id === variableId);
            const latestVariableData_ = (latestVariableData || {})[variableId] || [];
            return {
              ...variable,
              value: latestVariableData_[1],
              quality: latestVariableData_[2],
              name: name || sourceVariable.name || sourceVariable.id,
              unit: unit || sourceVariable.unit || '',
              variableDefaultUnit: sourceVariable.unit,
            };
          }),
        };
      }
      return pushPin;
    },
    [latestVariableData, stateSets, siteVariables]
  );

  const mapComponentValues = useCallback(
    (comp, state, tooltipConfig) => {
      const statefulVariables =
        pageType === 'stateset' ? (comp.variables || []).filter((v) => v.stateset_id) : [];
      const stateVariables = statefulVariables.map((variable) => {
        const sourceVariable = siteVariables.find((v) => v.id === variable.id);
        const latestVariableData_ = (latestVariableData || {})[variable.id] || [];
        return {
          ...variable,
          name: sourceVariable?.name || sourceVariable?.id || variable.name || variable.id,
          unit: sourceVariable?.unit || '',
          variableDefaultUnit: sourceVariable?.unit,
          value: latestVariableData_[1],
          quality: latestVariableData_[2],
        };
      });

      // Signals explicitly attached to this component's tooltip via page config
      // (componentTooltips), independent of pageType/statesets.
      const configuredVariables = (tooltipConfig?.variables || []).map((variable) => {
        const { variableId, name, unit } = variable;
        const sourceVariable = siteVariables.find((v) => v.id === variableId);
        const latestVariableData_ = (latestVariableData || {})[variableId] || [];
        return {
          ...variable,
          name: name || sourceVariable?.name || sourceVariable?.id || variableId,
          unit: unit || sourceVariable?.unit || '',
          variableDefaultUnit: sourceVariable?.unit,
          value: latestVariableData_[1],
          quality: latestVariableData_[2],
        };
      });

      if (pageType !== 'stateset' && !tooltipConfig) {
        return { displayName: comp.name, displayValue: comp.itemDesignation };
      }

      return {
        displayName: tooltipConfig?.displayName || comp.name,
        displayValue: tooltipConfig?.displayValue || comp.itemDesignation,
        freeText: tooltipConfig?.freeText,
        stateText: pageType === 'stateset' ? state.text : undefined,
        stateDescription: pageType === 'stateset' ? state.description : undefined,
        quality: pageType === 'stateset' ? state.quality : undefined,
        multipleActiveStates: pageType === 'stateset' && Boolean(state.multipleActiveStates),
        variables: [...stateVariables, ...configuredVariables],
      };
    },
    [latestVariableData, siteVariables, pageType]
  );

  const findActiveStates = useCallback(
    (component, variableData) =>
      findActiveStatesForVariables(component?.variables, variableData, stateSets),
    [stateSets]
  );

  const liveComponents = useMemo(
    () =>
      mappedComponents.map((comp) => {
        const compWithHealth = components.find((c) => c.id === comp.id);
        const isActive = focusedComponentIds.includes(comp.id);
        const hasActive = focusedComponentIds.length;

        const pushPin = (pushPins || []).find((p) => p.itemDesignation === comp.itemDesignation);
        const tooltipConfig = (componentTooltips || []).find(
          (t) => t.itemDesignation === comp.itemDesignation
        );
        const config = {
          isActive,
          hasActive,
          viewerClickedId,
          focusActiveComponent,
          showPushPins,
          displayErrorPins,
          renderComponents,
          highlightComponents,
          overlayComponents,
        };
        const showPin = shouldShowPin(compWithHealth || comp, pushPin, config);
        const pushPinWithValues = showPin ? mapPushPinValues(pushPin) : null;

        const activeStates =
          comp && comp.variables.length > 0 ? findActiveStates(comp, latestVariableData) : [];

        const activeState = activeStates.length === 1 ? activeStates[0] : {};

        const state =
          comp.type === 'virtual' ? { ...comp.stateSet?.states, area: true } : activeState;

        const highlight = shouldHighlightComponent(config);

        let tooltipState = activeStates[0] || {};
        if (comp.type === 'virtual') {
          tooltipState = state;
        } else if (activeStates.length > 1) {
          tooltipState = { text: 'Multiple active states found', multipleActiveStates: true };
        }
        const compToolTipWithValues = tooltipOnHover
          ? mapComponentValues(comp, tooltipState, tooltipConfig)
          : null;

        const overlay = highlight
          ? null
          : getComponentOverlay({ ...(compWithHealth || comp), state }, config, pageType);

        return {
          ...comp,
          componentId: comp.id,
          models: (comp.models || []).map((m) => ({
            ...m,
            ...models.find((sm) => sm.id === m.modelId),
          })),
          highlight,
          overlay,
          hide: !(highlight || overlay) && !shouldRenderComponent({ ...config, isolate }),
          showPin,
          pinStyle: getPinStyle({ ...(compWithHealth || comp), state }, pageType),
          pushpinTooltipContent: showPin
            ? () =>
                getPushpinTooltipContent(
                  compWithHealth || comp,
                  pushPinWithValues || {},
                  pageType,
                  timezone,
                  height
                )
            : null,
          componentsTooltipContent: tooltipOnHover
            ? () => getComponentsTooltipContent(compToolTipWithValues, timezone)
            : null,
        };
      }),
    [
      mappedComponents,
      components,
      focusedComponentIds,
      pushPins,
      componentTooltips,
      viewerClickedId,
      focusActiveComponent,
      showPushPins,
      displayErrorPins,
      renderComponents,
      highlightComponents,
      overlayComponents,
      mapPushPinValues,
      findActiveStates,
      latestVariableData,
      mapComponentValues,
      models,
      isolate,
      tooltipOnHover,
      stateSets,
      latestVariableData,
      pageType,
      clientSize,
      timezone,
    ]
  );

  const onGetAccessToken = useCallback(async (callback) => {
    const token = await getServiceToken('model');
    callback(token, 30);
  }, []);

  const onViewerCreated = useCallback(() => dispatch(setViewerReady(false)), []);

  const onGuiLoaded = useCallback(() => dispatch(setViewerReady(true)), []);

  const onModelChange = useCallback(() => {
    setModelLoading(true);
    dispatch(setViewerReady(false));
  }, []);

  const onModelChanged = useCallback(() => {
    setModelLoading(false);
    dispatch(setViewerReady(true));
  }, []);

  const handlePushPinClick = (component) => {
    if (globalThis.isMobile) {
      if (visibleTooltip === component.id) {
        setVisibleTooltip(null);
      } else {
        setVisibleTooltip(component.id);
      }
    }

    dispatch(setActiveComponentId(component.id));
  };

  const handleComponentClick = useCallback((component) => {
    if (component) {
      setViewerClickedId(component.id);
      dispatch(setActiveComponentId(component.id));
    } else {
      dispatch(setActiveComponentId(null));
      dispatch(setZoomComponentId(null));
    }
  }, []);

  const handleDismissProposeModelChange = () => {
    setProposeModelChange(() => ({ ...proposeModelChange, show: false, ignore: true }));
  };

  const confirmProposeModelChange = (modelId) => {
    setProposeModelChange(() => ({ model: activeModel, show: false, ignore: false }));
    dispatch(setActiveModelId(modelId));
  };

  const handleModelSelectorChange = useCallback(
    (modelId) => {
      dispatch(setActiveModelId(modelId));
    },
    [models, pageIndex]
  );

  const handleError = useCallback((errors) => {
    throw errors[0] || new Error('Viewer error');
  }, []);

  const viewerComponents = useMemo(
    () => liveComponents.filter((c) => c.mappingsByModel[activeModelId]),
    [liveComponents, activeModelId, clientSize]
  );

  const pins = useMemo(
    () =>
      viewerComponents
        .filter((c) => c.showPin)
        .map((comp) => (
          <PushPins.Pin
            onClick={() => handlePushPinClick(comp)}
            id={comp.id}
            position={comp.position}
            dbIds={comp.dbIds}
            objectIds={comp.objects.reduce((acc, object) => {
              acc.push(object.id);
              return acc;
            }, [])}
            key={comp.id}
            showTooltip={showActivePushpinTooltip && comp.id === activeComponentId}
            style={comp.pinStyle}
          >
            {(!globalThis.isMobile || (globalThis.isMobile && visibleTooltip === comp.id)) && (
              <PushPins.Tooltip>{comp.pushpinTooltipContent()}</PushPins.Tooltip>
            )}
          </PushPins.Pin>
        )),
    [viewerComponents, showActivePushpinTooltip]
  );

  const viewerTheme = theme === 'dark' ? THEME_AVT_DARK : THEME_AVT_BRIGHT;
  const viewerOptions = useMemo(() => ({ reverseMouseZoomDir: true }), []);

  if (properties.globalPermissionError) {
    return <PermissionDenied />;
  }

  return (
    <div
      ref={clientReference}
      className="viewer-component"
    >
      {proposeModelChange.show && !proposeModelChange.ignore && !modelLoading && (
        <div className="confirmation-dialog-container">
          <ConfirmationDialog
            modal={false}
            onCancel={handleDismissProposeModelChange}
            onConfirm={() => confirmProposeModelChange(proposeModelChange.model.modelId)}
            title="Change Model?"
            body={
              <p>
                The selected component does not exist in the current model.
                {hasSourceModels && (
                  <>
                    <br />
                    Do you want to switch to <span>{proposeModelChange.model.customName}</span>?
                  </>
                )}
              </p>
            }
            cancelText="Dismiss"
            confirmText="Change Model"
            confirmType="primary"
            showConfirm={!!hasSourceModels}
            slimButtons
          />
        </div>
      )}

      {models.length > 0 && versionConfig && activeModelId ? (
        <ModelViewer
          showUI={false}
          showViewer={display}
          alternateBranding={site?.siteId === 'non-identiq'}
          showProgress
          theme={viewerTheme}
          onGetAccessToken={onGetAccessToken}
          onViewerCreated={onViewerCreated}
          onGuiLoaded={onGuiLoaded}
          onModelChange={onModelChange}
          onModelChanged={onModelChanged}
          onViewerError={handleError}
          sceneConfig={versionConfig}
          options={viewerOptions}
          activeModelId={activeModelId}
        >
          <ModelSelector
            models={models}
            activeModelId={activeModelId}
            onModelChange={handleModelSelectorChange}
            closeable
          />
          <Components
            components={viewerComponents}
            model={activeModelId}
            onClick={handleComponentClick}
            isolate={isolate}
            selectionType={selectionType}
            disableSelection
            tooltipOnHover={tooltipOnHover}
            showAreaObjects={showAreaObjects}
            zoomTo={fitToIds}
          />
          <PushPins>{pins}</PushPins>
          <Gui key="avt-viewer-gui" />
          <HomeView
            key="avt-viewer-tool-home"
            showButton={toolbarOptions.includes('home')}
            buttonIndex={0}
          />
          <FirstPerson
            key="avt-viewer-tool-first-person"
            showButton={toolbarOptions.includes('firstperson')}
            buttonIndex={1}
          />
          <Measure
            key="avt-viewer-tool-measure"
            showButton={toolbarOptions.includes('measure')}
            buttonIndex={2}
          />
          <ViewerSettings
            key="avt-viewer-tool-settings"
            showButton={toolbarOptions.includes('settings')}
          />
        </ModelViewer>
      ) : null}
    </div>
  );
};

export default ViewerPanel;
